import type { OrgxWorkspace } from "./workspaces.js";
import {
  readWizardState,
  updateWizardState,
  type WizardAgentRosterEntry,
  type WizardAgentRosterRecord,
} from "./wizard-state.js";

export interface AgentRosterProfile {
  agentType: string;
  defaultDomain: string;
  name: string;
}

export interface AgentRosterAssignment {
  domain: string;
  name: string;
}

export const AGENT_ROSTER_PROFILES: AgentRosterProfile[] = [
  { agentType: "orchestrator-agent", defaultDomain: "orchestrator", name: "Xandy" },
  { agentType: "product-agent", defaultDomain: "product", name: "Pace" },
  { agentType: "engineering-agent", defaultDomain: "engineering", name: "Eli" },
  { agentType: "marketing-agent", defaultDomain: "marketing", name: "Mark" },
  { agentType: "sales-agent", defaultDomain: "sales", name: "Sage" },
  { agentType: "operations-agent", defaultDomain: "operations", name: "Orion" },
  { agentType: "design-agent", defaultDomain: "design", name: "Dana" },
];

export const AGENT_ROSTER_DOMAINS = AGENT_ROSTER_PROFILES.map((profile) => profile.defaultDomain);

export const DEFAULT_AGENT_ROSTER: WizardAgentRosterEntry[] = AGENT_ROSTER_PROFILES.map(
  ({ agentType, defaultDomain, name }) => ({
    agentType,
    domain: defaultDomain,
    name,
  }),
);

function findAgentRosterProfile(name: string): AgentRosterProfile | undefined {
  return AGENT_ROSTER_PROFILES.find((profile) => profile.name === name);
}

export function listAgentRosterProfiles(): AgentRosterProfile[] {
  return AGENT_ROSTER_PROFILES.map((profile) => ({ ...profile }));
}

export function buildAgentRosterEntries(
  assignments: AgentRosterAssignment[],
): WizardAgentRosterEntry[] {
  const seenNames = new Set<string>();

  return assignments.map((assignment) => {
    const name = assignment.name.trim();
    const domain = assignment.domain.trim();
    if (domain.length === 0) {
      throw new Error("OrgX agent roster domains must be non-empty.");
    }

    const profile = findAgentRosterProfile(name);
    if (!profile) {
      throw new Error(`Unknown OrgX agent '${assignment.name}'.`);
    }

    if (seenNames.has(profile.name)) {
      throw new Error(`OrgX agent '${profile.name}' can only be assigned once.`);
    }
    seenNames.add(profile.name);

    return {
      agentType: profile.agentType,
      domain,
      name: profile.name,
    };
  });
}

function toAgentRosterRecord(
  workspace: Pick<OrgxWorkspace, "id" | "name">,
  agents: WizardAgentRosterEntry[],
): WizardAgentRosterRecord {
  return {
    agents: agents.map((agent) => ({ ...agent })),
    configuredAt: new Date().toISOString(),
    workspaceId: workspace.id,
    workspaceName: workspace.name,
  };
}

export function configureAgentRoster(
  workspace: Pick<OrgxWorkspace, "id" | "name">,
  assignments: AgentRosterAssignment[],
  options: { statePath?: string } = {},
): WizardAgentRosterRecord {
  const agents = buildAgentRosterEntries(assignments);
  if (agents.length === 0) {
    throw new Error("Select at least one OrgX agent before saving the roster.");
  }

  const roster = toAgentRosterRecord(workspace, agents);
  updateWizardState(
    (current) => ({
      ...current,
      agentRoster: roster,
    }),
    options.statePath,
  );
  return roster;
}

export function ensureDefaultAgentRoster(
  workspace: Pick<OrgxWorkspace, "id" | "name">,
  options: { statePath?: string } = {},
): WizardAgentRosterRecord {
  const existing = readWizardState(options.statePath)?.agentRoster;
  if (existing?.workspaceId === workspace.id && existing.agents.length > 0) {
    return existing;
  }

  const roster = toAgentRosterRecord(workspace, DEFAULT_AGENT_ROSTER);
  updateWizardState(
    (current) => ({
      ...current,
      agentRoster: roster,
    }),
    options.statePath,
  );
  return roster;
}
